import { MobileDeviceUsageFilters } from '@reliance/types/filters/MobileDeviceUsageFilters';
import { Facility } from '@reliance/types/models/Facility';
import { Inmate } from '@reliance/types/models/Inmate';
import { MobileDevice, Usage } from '@reliance/types/models/MobileDevice';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { formatDate } from '../../format/DateTime';
import { formatDuration } from '../../format/Duration';
import { useMobileDeviceUsage, useMobileDeviceUsageByInmateId } from '../../hooks/MobileDeviceUsage';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../../table/BaseHtmlTable';

const columnHelper = createColumnHelper<Usage>();
const COLUMN_DEFS = [
  columnHelper.accessor((row) => formatDate(row.dateStarted), {
    id: 'dateStarted',
    header: () => 'Date'
  }),
  columnHelper.accessor('durationSeconds', {
    id: 'durationSeconds',
    header: () => 'Duration',
    cell: (row) => formatDuration(row.getValue())
  }),
  columnHelper.accessor('idleSeconds', {
    id: 'idleSeconds',
    header: () => 'Idle Time',
    cell: (row) => formatDuration(row.getValue())
  }),
  columnHelper.accessor('feature', {
    id: 'feature',
    header: () => 'Feature'
  }),
  columnHelper.display({
    id: 'item',
    header: () => 'Item',
    cell: (data) => {
      const usage = data.row.original;
      if (usage.musicSource) {
        return usage.musicSource.name;
      }
      if (usage.referenceMaterial) {
        return usage.referenceMaterial.name;
      }
      if (usage.inmateRequest) {
        return `${usage.inmateRequest.inmateRequestForm?.title} (#${usage.inmateRequest.id})`;
      }
      return '';
    }
  })
];

type TableProps = {
  inmate?: Inmate;
  facility?: Facility;
  mobileDevice?: MobileDevice;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

const LazyUsageTable: React.FC<TableProps & { inmate: Inmate }> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;
  const result = useMobileDeviceUsageByInmateId(null);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: result.execute
  });

  useNotifyTabLoading('device-usage', Boolean(tabSelected && result.loading));

  const rows = (result.result?.data ?? []) as Usage[];
  return <BaseHtmlTable columnDefs={COLUMN_DEFS} rows={rows} />;
};

const DirectUsageTable: React.FC<TableProps> = (props) => {
  const filters = useMemo<MobileDeviceUsageFilters>(() => ({
    facilityId: props.facility?.id,
    inmateId: props.inmate?.id,
    mobileDeviceId: props.mobileDevice?.id
  }), [props.inmate, props.facility, props.mobileDevice]);
  const usage = useMobileDeviceUsage(filters);
  const rows = ((usage.result || {}).data || []) as Usage[];
  return <BaseHtmlTable columnDefs={COLUMN_DEFS} rows={rows} />;
};

const Table: React.FC<TableProps> = (props) => {
  const useLazy = props.tabSelected !== undefined && props.inmate != null;
  if (useLazy && props.inmate) {
    return (
      <LazyUsageTable
        inmate={props.inmate}
        tabSelected={props.tabSelected}
        refreshTrigger={props.refreshTrigger}
      />
    );
  }
  return <DirectUsageTable {...props} />;
};

export default Table;
